import { Button, Grid, Stack, Typography } from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import PageContainer from 'components/common/PageContainer'
import PageSection from 'components/common/PageSection'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import Icon from 'components/Icon'
import { Header } from 'components/navigation/Header'
import { NotFound } from 'components/NotFound'
import { StatusCheckBreadcrumbs } from 'components/status-checks/common/StatusCheckBreadcrumbs'
import { EditStatusCheckFormContainer } from 'components/status-checks/edit/EditStatusCheckFormContainer'
import { DeleteStatusCheck } from 'components/status-checks/list/DeleteStatusCheck'
import { DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME } from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import toast from 'lib/toast'
import { useGetStatusChecks } from 'queries/status-checks/use-get-status-checks'
import { useGetStatusChecksAsServiceStatusChecks } from 'queries/status-checks/use-get-status-checks-as-service-status-checks'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { getMessageFromResponseOrError } from 'utils/strings'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'

function isServiceStatusCheck(
  item: unknown
): item is ServiceStatusCheckFromStatusCheck {
  return item instanceof Object && 'id' in item && 'integration' in item
}

export const EditStatusCheck: React.FC = () => {
  const statusChecksUrls = getStatusChecksUrls()
  const location = useLocation()
  const navigate = useNavigate()
  const { statusCheckId } = useParams()
  const { referrer = statusChecksUrls.base(), service } = location.state ?? {}
  const { refetch: reFetchStatusChecks } = useGetStatusChecks({
    enabled: false
  })

  const {
    data: existingStatusChecks,
    error: statusCheckError,
    isError: hasErrorStatusChecks,
    isLoading: isLoadingStatusChecks
  } = useGetStatusChecksAsServiceStatusChecks({
    retryOnMount: false,
    staleTime: DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME
  })

  /*
   * Use passed in status check value if cloning a status check. Otherwise, the status check being edited should be
   * inferred from the URL params
   */
  const selectedStatusCheck = existingStatusChecks?.find(
    (existingStatusCheck) => existingStatusCheck.id === statusCheckId
  )

  return (
    <>
      <Helmet title="Edit Health Check" />
      <Header>
        <StatusCheckBreadcrumbs
          endLabel="Edit Health Check"
          referrer={referrer}
          service={service}
        />
      </Header>
      <PageContainer>
        <PageSection
          errorMessage={getMessageFromResponseOrError(
            statusCheckError,
            'Unable to load Health Check data. Please try again.'
          )}
          hasError={hasErrorStatusChecks}
          isLoading={isLoadingStatusChecks}
        >
          {() => {
            if (!isServiceStatusCheck(selectedStatusCheck)) {
              return <NotFound />
            }

            return (
              <>
                <Stack sx={{ marginBottom: 2, rowGap: 2 }}>
                  <Grid container sx={{ columnGap: 2 }}>
                    <Grid item>
                      <Typography
                        data-test-edit-status-check-title
                        pb={0}
                        variant="h5"
                      >
                        {`Edit ${selectedStatusCheck.name}`}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Stack direction="row" spacing={2}>
                        <Button
                          data-test-clone-status-check
                          component={StyledRouterLink}
                          data-track-action="clone-status-check"
                          data-track-category="status-checks"
                          data-track-on="click"
                          fullWidth={false}
                          size="small"
                          startIcon={<Icon icon="clone" />}
                          state={{
                            statusCheck: {
                              ...selectedStatusCheck,
                              /*
                               * Unlike the clone button in `StatusCheckList` where the statusCheck passed
                               * in is of type StatusCheck, and will later get formatted to type 'ServiceStatusCheckFromStatusCheck',
                               * the selectedStatusCheck here is already of type 'ServiceStatusCheckFromStatusCheck',
                               * so we need to overwrite the id instead (and not identifier) since the consumer of
                               * this location state is checking the id field to determine whether the status check
                               * is a clone
                               */
                              id: null
                            }
                          }}
                          to={statusChecksUrls.new()}
                          variant="contained"
                        >
                          Clone
                        </Button>
                        <DeleteStatusCheck
                          statusCheck={selectedStatusCheck}
                          onChanged={async () => {
                            toast({
                              type: 'success',
                              text: `Successfully deleted Health Check ${selectedStatusCheck.name}`
                            })
                            await handleDataFetchOnChange(reFetchStatusChecks)
                            navigate(statusChecksUrls.base())
                          }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
                <EditStatusCheckFormContainer
                  statusCheck={selectedStatusCheck}
                />
              </>
            )
          }}
        </PageSection>
      </PageContainer>
    </>
  )
}
